import type { NotificationList } from "@closer/shared";
import {
  NOTIFICATION_CRITICALITY_NAMES,
  NOTIFICATION_CRITICALITY_TONES,
  PERMISSION_DOMAIN_NAMES,
  TENANT_TYPE_NAMES,
} from "@closer/shared";
import { Badge, Button, PageHeader, Skeleton } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";

import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { formatDateTime } from "../../lib/format";
import { usePreferences } from "../../lib/preferences";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * BİLDİRİM MERKEZİ — TAM LİSTE (CLOS-186 `[168]`).
 *
 * ### ⚠ BU EKRAN İLK TESLİMDE UNUTULDU
 *
 * CLOS-186'nın amacı *"...ve tek merkezde göstermek"*, kriteri *"Liste, panel, boş ve
 * hata durumları çalışır."* — "liste" ile "panel" AYRI sayılmış. İlk teslimde
 * projeksiyon, tablo, erişim süzgeci ve uç yazıldı; bu ekran yazılmadı. Sonuç: uçlar
 * çalışıyor, 5.500 test yeşil, kullanıcı beş bildirimlik önizlemeyi görüyor ve
 * ALTINCISINA HİÇBİR YERDEN ULAŞAMIYOR. Olmayan ekranın testi de olmadığı için hiçbir
 * bekçi bunu yakalayamadı (ekran boşluğu bekçisi: CLOS-863).
 *
 * ### ⚠ PANEL AKTİF ALANI GÖSTERİYOR, BU EKRAN DA
 *
 * Bekleyen İşlemler'in tam listesi (`[163]`) BÜTÜN alanları kapsıyor çünkü orada iş
 * kullanıcıya atanmış. Bildirim ise alana ait: uç `requireTenant` arkasında ve satırın
 * kendisi `tenant_id` taşıyor. Bu yüzden sorgu panelle AYNI anahtarı kullanıyor
 * (`tenantKey`) — iki ayrı anahtar olsaydı aynı veri iki kez çekilir, panelde okunmuş
 * görünen bir bildirim listede okunmamış kalabilirdi.
 *
 * ### ⚠ SATIRLAR OKUNDU İŞARETİ TAŞIYOR AMA DEĞİŞTİRMİYOR
 *
 * Kartın kriteri *"bildirimi okumak ilgili işlemi tamamlamaz"*. Bu ekran tek bir yazma
 * isteği bile göndermiyor; okundu işaretleme, tercihler ve e-posta teslimi CLOS-187'nin
 * işi. Yapısal sonuç: burada bir işlemin yanlışlıkla tamamlanması mümkün değil.
 *
 * ### ⚠ HEDEF BAĞLANTISI BİR ADRES, BİR KAPI DEĞİL
 *
 * `href` yalnız ekranı olan kayıt türleri için üretiliyor (`lib/notifications/titles.ts`)
 * ve hedef açılırken erişim yeniden soruluyor (CLOS-93). Kayıt bağı olmayan bildirim
 * (üyelik, abonelik) bağlantısız duruyor — tıklanacak bir yeri olmayan satır, yanlış yere
 * götüren satırdan iyidir.
 *
 * ### UI Tur 2 §7.16 — kısmi replatform
 *
 * PageHeader ve token ihlalleri (bg-warning/10, secondary→ghost aksiyon) düzeltildi.
 * Spec'in istediği tam `ListRow` (sol 3px accent şerit) replatformu YAPILMADI: satır
 * yapısı yukarıdaki "OKUNDU DÜĞMESİ BAĞLANTININ DIŞINDA" ve "HEDEF BAĞLANTISI" kararlarına
 * göre özenle kurulmuş (bağlantı+düğme iç içe olmayan iki ayrı etkileşimli öğe) — `ListRow`
 * bu ikisini aynı anda taşıyacak bir API sunmuyor. Riskli bir yeniden yazım yerine güvenli
 * yüzey iyileştirmesiyle sınırlı kalındı (ContractsPage FilterBar kararıyla aynı gerekçe).
 */

export function NotificationsPage() {
  const { activeTenant, activeTenantId } = useAuth();
  /**
   * ⚠ Tarih biçimi ORTAK YARDIMCIDAN ve kullanıcının kendi yerel ayarıyla
   * (`lib/format.ts`, `ActivityFeed` ile aynı desen). Burada elle `toLocaleDateString`
   * çağırmak iki kusur üretiyordu: biçim kararı ikinci bir yerde yaşıyordu ve tarih
   * kullanıcının tercihini değil sabit bir dili kullanıyordu.
   */
  const { preferences } = usePreferences();

  const sorgu = useQuery({
    queryKey: activeTenantId
      ? tenantKey(activeTenantId, "notifications")
      : ["t", "yok", "notifications"],
    enabled: activeTenantId !== null,
    queryFn: async () =>
      (await api<NotificationList>("/api/notifications", { tenantId: activeTenantId! })).data,
    /** Panelle AYNI politika (ADR-0037): aralık sunucudan, arka planda durur. */
    refetchInterval: (sorgu) => sorgu.state.data?.pollIntervalMs ?? false,
  });

  /**
   * Hata sunumu ORTAK KAPIDAN (`[067]` CLOS-106). Erişim kaybının METNİ domainin: 403
   * burada "bu çalışma alanına erişimin değişti" demek — panelle aynı cümle, çünkü aynı
   * olay.
   */
  const hataDurumu = sorgu.error
    ? resolveErrorState(sorgu.error, {
        idempotent: true,
        existenceIsSecret: false,
        overrides: {
          forbidden: {
            title: "Bu çalışma alanına erişiminiz değişti",
            description: "Bildirimleriniz görüntülenemiyor.",
          },
          error: {
            title: "Bildirimler yüklenemedi",
            description: "Bağlantı sorunları geçici olabilir.",
          },
        },
      })
    : null;

  const queryClient = useQueryClient();

  /**
   * ⚠ OKUNDU YAZMASI SUNUCUYA, SAYAÇ SUNUCUDAN (CLOS-187 `[169]`).
   *
   * İstemci kendi sayacını düşürmüyor; yazma bitince sorgu geçersiz kılınıyor ve sayı
   * yeniden sunucudan geliyor. Yerel düşürme yapsaydık, kartın *"okundu durumu bütün
   * aktif oturumlarda eşitlenir"* kriteri yalnız BU sekmede sağlanır, ikinci sekme eski
   * sayıyı göstermeye devam ederdi — polling aralığı kadar bile olsa tutarsızlık.
   */
  const anahtar = activeTenantId
    ? tenantKey(activeTenantId, "notifications")
    : ["t", "yok", "notifications"];

  const okunduYap = useMutation({
    mutationFn: (id: string) =>
      api(`/api/notifications/${id}/read`, {
        method: "POST",
        tenantId: activeTenantId ?? undefined,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: anahtar }),
  });

  const tumunuOkunduYap = useMutation({
    mutationFn: () =>
      api("/api/notifications/read-all", {
        method: "POST",
        tenantId: activeTenantId ?? undefined,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: anahtar }),
  });

  const bildirimler = sorgu.data?.items ?? [];
  /** ⚠ Rozetle AYNI kaynak: sayı sunucudan, listeden sayılmıyor (CLOS-187). */
  const okunmamis = sorgu.data?.unread ?? 0;
  /** Elde veri VAR ama son tazeleme düştü — panelle aynı "eski veri" durumu. */
  const eskiVeri = sorgu.isError && sorgu.data !== undefined;

  return (
    <section className="flex w-full flex-col gap-6">
      <PageHeader
        title="Bildirimler"
        description={
          activeTenant
            ? `${activeTenant.name} · ${TENANT_TYPE_NAMES[activeTenant.type]} çalışma alanındaki gelişmeler.`
            : "Çalışma alanınızdaki gelişmeler."
        }
        /* Sayı erişilebilir metinde de var: rozet yalnız görsel olsaydı ekran okuyucu kaçırırdı. */
        meta={
          okunmamis > 0 ? (
            <p className="text-sm text-fg-soft">{okunmamis} okunmamış bildirim.</p>
          ) : undefined
        }
        actions={
          okunmamis > 0 ? (
            /* ⚠ Kapsam AKTİF ALAN: kullanıcı "tümü" derken gördüğü listeyi kastediyor. */
            <Button
              size="sm"
              variant="ghost"
              disabled={tumunuOkunduYap.isPending}
              onClick={() => tumunuOkunduYap.mutate()}
            >
              Tümünü okundu yap
            </Button>
          ) : undefined
        }
      />

      {/* Eski veri UYARISI listenin ÜSTÜNDE: kullanıcı içeriği okumadan önce ne kadar
          güvenebileceğini bilmeli (panelle aynı karar). */}
      {eskiVeri ? (
        <p role="status" className="rounded-md bg-warning-soft px-3 py-2 text-sm text-fg-strong">
          Bağlantı yenilenemedi, eski veri gösteriliyor
        </p>
      ) : null}

      {sorgu.isPending ? (
        <div role="status" aria-label="Bildirimler yükleniyor">
          <Skeleton lines={5} />
        </div>
      ) : hataDurumu && !eskiVeri ? (
        <StateView state={hataDurumu} onRetry={() => void sorgu.refetch()} />
      ) : bildirimler.length === 0 ? (
        /*
          ⚠ BOŞ DURUM SUÇLAMIYOR, ANLATIYOR (Bekleyen İşlemler'in 26 Ağustos dersi).

          "Bildirim yok" tek başına kullanıcıya bir şeyin bozuk olup olmadığını
          söylemiyor. Bildirimin NEREDEN doğduğunu söylemek, boş listeyi bir arıza
          şüphesi olmaktan çıkarıyor.
        */
        <div className="flex flex-col items-start gap-2 rounded-lg border border-border-default p-4">
          <p className="text-sm font-medium text-fg-strong">Bildiriminiz yok.</p>
          <p className="text-sm text-fg-muted">
            Bildirimler kendiliğinden doğar: bir sözleşmede sizden bahsedildiğinde, bir kayda
            erişiminiz açıldığında ya da çalışma alanınıza üye eklendiğinde burada görünürler.
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {bildirimler.map((b) => {
            const govde = (
              <>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="flex items-center gap-2">
                    {/* Okunmamış işareti METİNLE de taşınıyor: renk tek başına ayırt edici
                        olsaydı renk körü kullanıcı farkı göremezdi (panelle aynı karar). */}
                    {b.readAt === null ? <Badge tone="danger">Yeni</Badge> : null}
                    {/* ⚠ Önem seviyesi panelle AYNI kurala göre çiziliyor (CLOS-1244):
                        `INFO` rozetsiz, diğerleri metinli rozet. İki yüzey aynı bildirimi
                        farklı anlatırsa kullanıcı hangisine güveneceğini bilemez. */}
                    {NOTIFICATION_CRITICALITY_NAMES[b.criticality] ? (
                      <Badge
                        tone={NOTIFICATION_CRITICALITY_TONES[b.criticality]}
                        data-testid={`bildirim-seviye-${b.id}`}
                      >
                        {NOTIFICATION_CRITICALITY_NAMES[b.criticality]}
                      </Badge>
                    ) : null}
                    <span className="text-xs text-fg-soft">
                      {PERMISSION_DOMAIN_NAMES[b.category]}
                    </span>
                  </span>
                  <span className="text-sm text-fg">{b.title}</span>
                  {/*
                    ⚠ KAYIT ADI AYRI SATIRDA, BAŞLIĞA GÖMÜLÜ DEĞİL (CLOS-232 dönüşü).

                    Ad sunucuda okuma anında, erişim süzgecinden SONRA çözülüyor
                    (`notifications/query.ts`) — bildirim satırında saklanmıyor. Burada
                    ayrı bir satır olması iki şeyi koruyor: başlık hâlâ kanonik olay
                    cümlesi (arama ve gruplama onun üzerinden), ad ise yokluğunda cümleyi
                    bozmayan bir ek. Kayıt bağı olmayan bildirimde `null` gelir ve satır
                    hiç çizilmez.
                  */}
                  {b.recordName ? (
                    <span className="truncate text-xs text-fg-soft">{b.recordName}</span>
                  ) : null}
                </div>
                <span className="shrink-0 text-xs text-fg-soft">
                  {formatDateTime(b.createdAt, preferences.locale)}
                </span>
              </>
            );

            /**
             * ⚠ OKUNDU DÜĞMESİ BAĞLANTININ DIŞINDA — ve bu bilinçli.
             *
             * Düğmeyi bağlantının İÇİNE koysaydık tıklama hem okundu işaretler hem
             * kayda giderdi; kullanıcı "sadece okundu yapmak" isteyemezdi. Ayrıca iç içe
             * etkileşimli öğe erişilebilirlik ağacında bozuk bir yapı üretir.
             *
             * ⚠ Bağlantıya tıklamak okundu YAPMIYOR: kartın kriteri okumayı bir haber
             * alma sayıyor, kaydı açmayı değil. Kullanıcı kaydı açtı diye bildirimi
             * gördüğünü varsaymak, listedeki başka satırları da sessizce kapatabilirdi.
             */
            const okunduDugmesi =
              b.readAt === null ? (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={okunduYap.isPending}
                  onClick={() => okunduYap.mutate(b.id)}
                >
                  Okundu
                </Button>
              ) : null;

            return (
              <li key={b.id} data-testid="bildirim-satiri" className="flex items-center gap-2">
                {b.href ? (
                  <Link
                    to={b.href}
                    className="flex flex-1 flex-wrap items-center justify-between gap-2 rounded-md border border-border-default px-3 py-2 hover:bg-muted"
                  >
                    {govde}
                  </Link>
                ) : (
                  /* Bağlantısız satır da AYNI kutuda duruyor: farklı görünseydi kullanıcı
                     eksik bir şey olduğunu sanırdı — oysa o bildirimin gidilecek bir kaydı
                     yok. */
                  <div className="flex flex-1 flex-wrap items-center justify-between gap-2 rounded-md border border-border-default px-3 py-2">
                    {govde}
                  </div>
                )}
                {okunduDugmesi}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
